import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Button, Typography, Drawer, Divider, TextField, IconButton, Chip, Checkbox, FormControlLabel, Tooltip, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import {
  useGetUserNotesBySepQuery,
  useCreateUserNoteMutation,
  useUpdateUserNoteMutation,
  useDeleteUserNoteMutation,
} from '../../services/userNoteSlice/userNoteSlice';
import { SEPSearchRow } from '../../../shared/types/SEP';
import { UserNote } from '../../../shared/types/UserNote';
import ArchitectureIcon from '../../assets/svg/ArchitectureIcon';
import { typography, typographyTokens } from '../../theme/typographyTokens';
import { spacingTokens } from '../../theme/spacingTokens';

// Special SEP ID for storing user templates (not bound to specific SEP)
const TEMPLATE_SEP_ID = 20000;

interface SepNotesDrawerProps {
  open: boolean;
  onClose: () => void;
  row: SEPSearchRow;
  /** Hide the "OPEN SEP" button when drawer is opened from SEP page */
  hideOpenSepButton?: boolean;
}

interface AboutMyNotesTooltipProps {
  placement?: 'left' | 'right' | 'top' | 'bottom';
}

const AboutMyNotesTooltip: React.FC<AboutMyNotesTooltipProps> = ({ placement = 'left' }) => {
  const theme = useTheme();

  return (
    <Tooltip
      title={(
        <Typography sx={{ fontSize: typography.body2.fontSize, lineHeight: typographyTokens.lineHeights.relaxed }}>
          Use "My Notes" as your personal workspace for capturing reminders, questions, follow-up items, or other notes related to this SEP request. Notes entered here are visible only to you and are not shared with reviewers, approvers, or other participants. "My Notes" are not included in the completed SEP assessment and are not considered part of the SEP auditable record.
        </Typography>
      )}
      arrow
      placement={placement}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.prime.blue,
          fontWeight: typographyTokens.fontWeights.semibold,
          fontSize: typography.caption.fontSize,
          cursor: 'help',
        }}
      >
        About My Notes
      </Typography>
    </Tooltip>
  );
};

/**
 * Drawer component for viewing/editing multiple notes per SEP.
 * Has two views: List View (default) and Editor View.
 */
const SepNotesDrawer: React.FC<SepNotesDrawerProps> = ({ open, onClose, row, hideOpenSepButton = false }) => {
  const theme = useTheme();
  // Ref for the RichTextEditor (ReactQuill)
  const editorRef = useRef<ReactQuill | null>(null);
  // View mode: 'list' or 'editor'
  const [viewMode, setViewMode] = useState<'list' | 'editor'>('list');
  // Currently selected note ID (null = new note mode)
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
  const [noteTitle, setNoteTitle] = useState<string>('');
  const [noteContent, setNoteContent] = useState<string>('');
  const [isTemplate, setIsTemplate] = useState<boolean>(false);
  const [isPinned, setIsPinned] = useState<boolean>(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isNewNote, setIsNewNote] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    noteId: number | null;
    isTemplate: boolean;
    title: string;
  }>({ show: false, noteId: null, isTemplate: false, title: '' });

  // Fetch existing notes for this SEP (regular notes only)
  const { data: sepNotes = [], isLoading } = useGetUserNotesBySepQuery(row.id, {
    skip: !open,
  });

  // Fetch user templates (stored with special SEP ID)
  const { data: templateNotes = [], isLoading: isLoadingTemplates } = useGetUserNotesBySepQuery(TEMPLATE_SEP_ID, {
    skip: !open,
  });

  const [createNote, { isLoading: isCreating }] = useCreateUserNoteMutation();
  const [updateNote, { isLoading: isUpdating }] = useUpdateUserNoteMutation();
  const [deleteNote, { isLoading: isDeleting }] = useDeleteUserNoteMutation();

  // Regular notes from current SEP (exclude any with template=1)
  const regularNotes = useMemo(() => {
    return sepNotes.filter((note) => note.template !== 1);
  }, [sepNotes]);

  // Templates from the special template SEP ID (only template=1)
  const templates = useMemo(() => {
    return templateNotes.filter((note) => note.template === 1);
  }, [templateNotes]);

  // Get the currently selected note object (check both regular notes and templates)
  const selectedNote = useMemo(() => {
    return regularNotes.find((n) => n.id === selectedNoteId)
      || templates.find((n) => n.id === selectedNoteId)
      || null;
  }, [regularNotes, templates, selectedNoteId]);

  // Auto-select note when notes load - prefer pinned note if multiple exist
  useEffect(() => {
    if (!isLoading && regularNotes.length > 0 && !selectedNoteId && !isNewNote) {
      // Find pinned note, otherwise use first note
      const pinnedNote = regularNotes.find((n) => n.pinned);
      setSelectedNoteId(pinnedNote ? pinnedNote.id : regularNotes[0].id);
    }
  }, [regularNotes, isLoading, selectedNoteId, isNewNote]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setViewMode('list');
      setSelectedNoteId(null);
      setNoteTitle('');
      setNoteContent('');
      setIsTemplate(false);
      setIsPinned(false);
      setHasChanges(false);
      setLastSaved(null);
      setIsNewNote(false);
    }
  }, [open]);

  // Initialize content when a note is selected (for editor mode)
  useEffect(() => {
    if (selectedNote && !isNewNote) {
      setNoteTitle(selectedNote.title || '');
      setNoteContent(selectedNote.content || '');
      setIsTemplate(selectedNote.template === 1);
      setIsPinned(selectedNote.pinned || false);
      setLastSaved(selectedNote.updatedAt);
      setHasChanges(false);
    } else if (isNewNote) {
      setNoteTitle('');
      setNoteContent('');
      setIsPinned(false);
      setLastSaved(null);
      setHasChanges(false);
    }
  }, [selectedNote, isNewNote]);

  // Track title changes
  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNoteTitle(e.target.value);
    setHasChanges(true);
  }, []);

  // Track content changes
  const handleContentChange = useCallback((value: string) => {
    setNoteContent(value);
    setHasChanges(true);
  }, []);

  // Track pinned checkbox changes
  const handlePinnedChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPinned(e.target.checked);
    setHasChanges(true);
  }, []);

  // Save note (create or update)
  const saveNote = useCallback(async () => {
    const strippedContent = noteContent.replace(/<[^>]*>/g, '').trim();

    // Don't save if no content
    if (!strippedContent) {
      return null;
    }

    // Use TEMPLATE_SEP_ID for templates, otherwise use current SEP ID
    const targetSepID = isTemplate ? TEMPLATE_SEP_ID : row.id;

    try {
      if (isNewNote) {
        // Create new note
        const newNote = await createNote({
          sepID: targetSepID,
          title: noteTitle.trim() || undefined,
          content: noteContent,
          template: isTemplate ? 1 : 0,
          pinned: isPinned,
        }).unwrap();
        setLastSaved(new Date().toISOString());
        setHasChanges(false);
        setIsNewNote(false);
        setSelectedNoteId(newNote.id);
        return newNote;
      } else if (selectedNote) {
        // Use TEMPLATE_SEP_ID if user checked "Save as Template", otherwise use current SEP ID
        const updateSepID = isTemplate ? TEMPLATE_SEP_ID : row.id;
        // Update existing note
        await updateNote({
          id: selectedNote.id,
          sepID: updateSepID,
          title: noteTitle.trim() || null,
          content: noteContent,
          template: isTemplate ? 1 : 0,
          pinned: isPinned,
        }).unwrap();
        setLastSaved(new Date().toISOString());
        setHasChanges(false);
        return selectedNote;
      }
    } catch (error) {
      // Error handled by RTK Query
    }
    return null;
  }, [noteContent, noteTitle, isNewNote, selectedNote, row.id, updateNote, createNote, isTemplate, isPinned]);

  // Load template content into editor (from list view - creates new note)
  const handleUseTemplate = useCallback(async (template: UserNote) => {
    if (hasChanges) {
      await saveNote();
    }
    setSelectedNoteId(null);
    setIsNewNote(true);
    setNoteTitle('');
    setNoteContent(template.content);
    setIsTemplate(false);
    setLastSaved(null);
    setHasChanges(true);
    setViewMode('editor');

    // Use setTimeout to ensure the editor is mounted before setting content via editor API
    setTimeout(() => {
      if (editorRef.current) {
        const editor = editorRef.current.getEditor();
        // Clear existing content and insert template content
        editor.setText('');
        editor.clipboard.dangerouslyPasteHTML(0, template.content);
        // Sync state with editor content
        const updatedContent = editor.root.innerHTML;
        setNoteContent(updatedContent);
      }
    }, 100);
  }, [hasChanges, saveNote]);

  // Load template content into current editor at cursor position (content only, not title)
  const handleLoadTemplateContent = useCallback((template: UserNote) => {
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      const selection = editor.getSelection();
      const cursorIndex = selection ? selection.index : editor.getLength() - 1;

      // Insert template content at cursor position
      editor.clipboard.dangerouslyPasteHTML(cursorIndex, template.content);

      // Get the updated content from the editor
      const updatedContent = editor.root.innerHTML;
      setNoteContent(updatedContent);
    } else {
      // Fallback: append to end if no editor ref
      setNoteContent((prev) => prev + template.content);
    }
    setHasChanges(true);
  }, []);

  // Handle switching to a different note
  const handleSelectNote = useCallback(async (noteId: number) => {
    if (hasChanges) {
      await saveNote();
    }
    setIsNewNote(false);
    setSelectedNoteId(noteId);
  }, [hasChanges, saveNote]);

  // Handle creating a new note - goes to editor
  const handleNewNote = useCallback(async () => {
    if (hasChanges) {
      await saveNote();
    }
    setSelectedNoteId(null);
    setIsNewNote(true);
    setNoteTitle('');
    setNoteContent('');
    setIsTemplate(false);
    setLastSaved(null);
    setHasChanges(false);
    setViewMode('editor');
  }, [hasChanges, saveNote]);

  // Handle creating a new template - goes to editor in template mode
  const handleNewTemplate = useCallback(async () => {
    if (hasChanges) {
      await saveNote();
    }
    setSelectedNoteId(null);
    setIsNewNote(true);
    setNoteTitle('');
    setNoteContent('');
    setIsTemplate(true);
    setIsPinned(false);
    setLastSaved(null);
    setHasChanges(false);
    setViewMode('editor');
  }, [hasChanges, saveNote]);

  // Handle edit button - switch to editor view
  const handleEditNote = useCallback(() => {
    setViewMode('editor');
  }, []);

  // Handle back to list
  const handleBackToList = useCallback(async () => {
    if (hasChanges) {
      await saveNote();
    }
    setViewMode('list');
    // If was creating new note and nothing saved, go back to first note
    if (isNewNote && !selectedNoteId && regularNotes.length > 0) {
      setSelectedNoteId(regularNotes[0].id);
      setIsNewNote(false);
    }
  }, [hasChanges, saveNote, isNewNote, selectedNoteId, regularNotes]);

  // Handle drawer close
  const handleClose = useCallback(async () => {
    if (hasChanges) {
      await saveNote();
    }
    onClose();
  }, [hasChanges, saveNote, onClose]);

  // Handle delete note - show confirmation modal first
  const handleDeleteNote = useCallback((noteId: number, isTemplateNote = false) => {
    // Look up title for confirmation dialog
    const noteToDelete = isTemplateNote
      ? templates.find((t) => t.id === noteId)
      : regularNotes.find((n) => n.id === noteId);

    setDeleteConfirmation({
      show: true,
      noteId,
      isTemplate: isTemplateNote,
      title: noteToDelete?.title || 'Untitled',
    });
  }, [templates, regularNotes]);

  // Confirm and execute delete
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmation.noteId) return;

    const { noteId, isTemplate } = deleteConfirmation;
    const deleteSepID = isTemplate ? TEMPLATE_SEP_ID : row.id;
    const isDeletingSelected = noteId === selectedNoteId;

    try {
      await deleteNote({ id: noteId, sepID: deleteSepID, template: isTemplate, title: deleteConfirmation.title }).unwrap();

      // If we deleted the selected note, select another one
      if (isDeletingSelected) {
        if (isTemplate) {
          // Find another template to select
          const remainingTemplates = templates.filter((t) => t.id !== noteId);
          if (remainingTemplates.length > 0) {
            setSelectedNoteId(remainingTemplates[0].id);
          } else if (regularNotes.length > 0) {
            setSelectedNoteId(regularNotes[0].id);
          } else {
            setSelectedNoteId(null);
          }
        } else {
          // Find another regular note to select
          const remainingNotes = regularNotes.filter((n) => n.id !== noteId);
          if (remainingNotes.length > 0) {
            // Prefer pinned note if available
            const pinnedNote = remainingNotes.find((n) => n.pinned);
            setSelectedNoteId(pinnedNote ? pinnedNote.id : remainingNotes[0].id);
          } else {
            setSelectedNoteId(null);
          }
        }
      }
    } catch (error) {
      // Error handled by RTK Query
    } finally {
      setDeleteConfirmation({ show: false, noteId: null, isTemplate: false, title: '' });
    }
  }, [deleteConfirmation, deleteNote, row.id, selectedNoteId, regularNotes, templates]);

  // Cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation({ show: false, noteId: null, isTemplate: false, title: '' });
  }, []);

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Calculate word count
  const wordCount = noteContent
    .replace(/<[^>]*>/g, '')
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

  // Render the Editor View
  const renderEditorView = () => (
    <>
      {/* Back Button */}
      <Box sx={{ px: spacingTokens.md, py: spacingTokens.sm, borderBottom: `1px dashed ${theme.palette.primeAdditional.lightBlue}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          sx={{
            color: theme.palette.prime.blue,
            fontSize: typography.body2.fontSize,
            fontWeight: typographyTokens.fontWeights.semibold,
            textTransform: 'none',
            p: 0,
          }}
          onClick={handleBackToList}
          disabled={isCreating || isUpdating}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: theme.palette.prime.blue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: spacingTokens.sm,
            }}
          >
            <i
              className="fa-solid fa-arrow-left"
              style={{ fontSize: typographyTokens.fontSizes.xxs, color: theme.palette.white.main }}
            ></i>
          </Box>
          SAVE & BACK
          {(isCreating || isUpdating) && ' (Saving...)'}
        </Button>

        <AboutMyNotesTooltip placement="left" />
      </Box>

      {/* Editor Title */}
      <Box
        sx={{
          py: spacingTokens.sm,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mx: spacingTokens.md,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>
            {isTemplate
              ? (isNewNote ? 'New Template' : 'Edit Template')
              : (isNewNote ? 'New Note' : 'Edit Note')}
          </Typography>
          {isTemplate && (
            <Typography variant="caption" sx={{ color: theme.palette.prime.orange, fontSize: typographyTokens.fontSizes.xs }}>
              Template mode: this will be saved to Templates
            </Typography>
          )}
        </Box>
      </Box>

      {/* Title Input with Template and Pinned Checkboxes */}
      <Box sx={{ px: spacingTokens.md, pb: spacingTokens.sm, display: 'flex', alignItems: 'center', gap: spacingTokens.sm }}>
        <TextField
          placeholder="Note title (optional)"
          value={noteTitle}
          onChange={handleTitleChange}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              fontSize: typography.body1.fontSize,
              fontWeight: typographyTokens.fontWeights.semibold,
            },
          }}
        />
        <Tooltip title="This note will be shown first when opening notes for this SEP" arrow placement="top">
          <FormControlLabel
            control={
              <Checkbox
                checked={isPinned}
                onChange={handlePinnedChange}
                size="small"
                sx={{ color: theme.palette.prime.orange, '&.Mui-checked': { color: theme.palette.prime.orange } }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontSize: typographyTokens.fontSizes.xs, color: theme.palette.prime.granite, display: 'flex', alignItems: 'center' }}>
                <i className="fa-solid fa-thumbtack" style={{ marginRight: '3px', fontSize: typographyTokens.fontSizes.xxs }}></i>
                Default
              </Typography>
            }
            sx={{ m: 0, whiteSpace: 'nowrap' }}
          />
        </Tooltip>
      </Box>

      {/* Notes Content Area - Rich Text Editor */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          mx: spacingTokens.md,
          minHeight: 0,
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <RichTextEditor
            ref={editorRef}
            value={noteContent}
            setValue={handleContentChange}
            placeholder="Write your notes here..."
            style={{ minHeight: '200px', flex: 1, marginBottom: '0px', paddingBottom: '0px', overflow: 'auto' }}
          />
        </Box>
      </Box>

      {/* Last Saved Timestamp & Word Count */}
      <Box sx={{ px: spacingTokens.md, py: spacingTokens.sm, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: theme.palette.elements.alert, fontSize: typographyTokens.fontSizes.xs }}>
          {lastSaved
            ? `Last Saved: ${formatDate(lastSaved)}`
            : hasChanges
              ? 'Unsaved changes'
              : isNewNote
                ? 'New note'
                : 'No saved notes yet'}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {wordCount} words
        </Typography>
      </Box>

      {/* Templates Section in Editor */}
      {templates.length > 0 && (
        <>
          <Divider sx={{ mx: spacingTokens.md }} />
          <Box sx={{ px: spacingTokens.md, py: spacingTokens.sm }}>
            <Typography variant="caption" sx={{ fontWeight: typographyTokens.fontWeights.semibold, color: theme.palette.prime.granite, textTransform: 'uppercase', mb: spacingTokens.sm, display: 'block' }}>
              Load from Template
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacingTokens.sm }}>
              {templates.map((template) => (
                <Chip
                  key={template.id}
                  label={template.title || 'Untitled Template'}
                  size="small"
                  onClick={() => handleLoadTemplateContent(template)}
                  icon={
                    <i
                      className="fa-solid fa-file-import"
                      style={{ fontSize: typographyTokens.fontSizes.xxs, color: theme.palette.prime.orange, marginLeft: spacingTokens.sm }}
                    ></i>
                  }
                  sx={{
                    cursor: 'pointer',
                    bgcolor: theme.palette.supportingAdditional.cantaloupeUltraLight,
                    color: theme.palette.prime.orange,
                    fontWeight: typographyTokens.fontWeights.regular,
                    px: spacingTokens.xs,
                    border: `1px solid ${theme.palette.prime.orange}`,
                    fontSize: typography.caption.fontSize,
                    '&:hover': {
                      bgcolor: theme.palette.supportingAdditional.cantaloupeContainerFill,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </>
      )}
    </>
  );

  // Render the List View
  const renderListView = () => (
    <>
      {/* Close Button */}
      <Box sx={{ px: spacingTokens.md, py: spacingTokens.sm, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          sx={{
            color: theme.palette.prime.blue,
            fontSize: typography.body2.fontSize,
            fontWeight: typographyTokens.fontWeights.semibold,
            textTransform: 'none',
            p: 0,
          }}
          onClick={handleClose}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: theme.palette.prime.blue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: spacingTokens.sm,
            }}
          >
            <i
              className="fa-solid fa-x"
              style={{ fontSize: typographyTokens.fontSizes.xxs, color: theme.palette.white.main }}
            ></i>
          </Box>
          CLOSE
        </Button>

        <AboutMyNotesTooltip placement="left" />
      </Box>

      {/* SEP Header Section */}
      <Box
        sx={{
          px: spacingTokens.md,
          py: spacingTokens.sm,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: `2px dashed ${theme.palette.primeAdditional.lightBlue}`,
          borderBottom: `2px dashed ${theme.palette.primeAdditional.lightBlue}`,
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.white.main,
          color: theme.palette.mode === 'dark' ? theme.palette.white.main : theme.palette.background.default,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: spacingTokens.md }}>
          <Box sx={{ textAlign: 'center', minWidth: 60 }}>
            <Box
              sx={{
                pt: spacingTokens.xs,
                pb: spacingTokens.xs,
                px: spacingTokens.xs,
                border: `2px dashed ${theme.palette.primeAdditional.lightBlue}`,
              }}
            >
              <ArchitectureIcon height={24} width={24} color={theme.palette.prime.blue} />
            </Box>
            <Typography
              variant="caption"
              display="block"
              sx={{ fontSize: typographyTokens.fontSizes.micro, color: theme.palette.prime.blue, lineHeight: typographyTokens.lineHeights.tight, mt: spacingTokens.xs }}
            >
              {row.phase === 'validation'
                ? 'Validation'
                : row.phase.charAt(0).toUpperCase() + row.phase.slice(1)}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                color: theme.palette.prime.blue,
                fontWeight: typographyTokens.fontWeights.semibold,
                fontSize: typography.body2.fontSize,
                textTransform: 'uppercase',
              }}
            >
              {row.workFlowType || 'SEP Design Review'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.prime.blue, fontSize: typography.caption.fontSize }}>
              #{row.id} &nbsp;&nbsp;&nbsp; {row.name || 'N/A'}
            </Typography>
          </Box>
        </Box>
        {!hideOpenSepButton && (
          <Button
            component={Link}
            to={`/sep/${row.id}`}
            variant="outlined"
            sx={{
              color: theme.palette.prime.blue,
              borderColor: theme.palette.prime.blue,
              textTransform: 'none',
              fontWeight: typographyTokens.fontWeights.semibold,
              fontSize: typography.caption.fontSize,
              px: spacingTokens.md,
            }}
          >
            OPEN SEP{' '}
            <i
              className="fa-solid fa-arrow-up-right-from-square"
              style={{ marginLeft: spacingTokens.xs, fontSize: typographyTokens.fontSizes.xxs }}
            ></i>
          </Button>
        )}
      </Box>

      {/* All Notes Section - Full Width (moved above title) */}
      <Box sx={{ px: spacingTokens.md, py: spacingTokens.sm }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: spacingTokens.sm }}>
          <Typography variant="caption" sx={{ fontWeight: typographyTokens.fontWeights.semibold, color: theme.palette.prime.granite, textTransform: 'uppercase' }}>
            All Notes ({regularNotes.length})
          </Typography>
          <Button
            size="small"
            sx={{
              color: theme.palette.prime.blue,
              textTransform: 'none',
              fontWeight: typographyTokens.fontWeights.semibold,
              fontSize: typographyTokens.fontSizes.xs,
              p: 0,
              minWidth: 'auto',
            }}
            onClick={handleNewNote}
            disabled={isCreating || isUpdating}
          >
            <i className="fa-solid fa-plus" style={{ marginRight: spacingTokens.xs, fontSize: typographyTokens.fontSizes.xxs }}></i>
            Add New Note
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacingTokens.sm, maxHeight: '100px', overflowY: 'auto' }}>
          {regularNotes.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No notes yet.
            </Typography>
          ) : (
            regularNotes.map((note) => {
              const isActive = note.id === selectedNoteId;
              const noteDisplayTitle = note.title || 'Untitled';
              return (
                <Box key={note.id} sx={{ position: 'relative', display: 'inline-flex' }}>
                  <Chip
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: spacingTokens.xs }}>
                        {note.pinned && (
                          <i className="fa-solid fa-thumbtack" style={{ fontSize: typographyTokens.fontSizes.micro, color: isActive ? theme.palette.white.main : theme.palette.prime.orange }}></i>
                        )}
                        {noteDisplayTitle}
                      </Box>
                    }
                    size="small"
                    onClick={() => handleSelectNote(note.id)}
                    disabled={isDeleting}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isActive ? theme.palette.prime.blue : theme.palette.primeAdditional.lightBlue,
                      color: isActive ? theme.palette.white.main : theme.palette.prime.blue,
                      fontWeight: isActive ? typographyTokens.fontWeights.semibold : typographyTokens.fontWeights.regular,
                      px: spacingTokens.sm,
                      pr: spacingTokens.lg,
                      fontSize: typography.caption.fontSize,
                      '&:hover': {
                        bgcolor: isActive ? theme.palette.content.default : theme.palette.primeAdditional.lightBlue,
                      },
                    }}
                  />
                  <Tooltip title="Delete" arrow placement="top">
                    <Box
                      className="delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNote(note.id);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: theme.palette.elements.alert,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        transition: 'opacity 0.2s, background-color 0.2s',
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 1,
                          bgcolor: theme.palette.deepRed.dark,
                        },
                      }}
                    >
                      <i className="fa-solid fa-times" style={{ fontSize: typographyTokens.fontSizes.micro, color: theme.palette.white.main }}></i>
                    </Box>
                  </Tooltip>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      <Divider sx={{ mx: spacingTokens.md }} />

      {/* Note Title with Edit Button */}
      <Box
        sx={{
          py: spacingTokens.sm,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mx: spacingTokens.md,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: typographyTokens.fontWeights.semibold, display: 'flex', alignItems: 'center', gap: spacingTokens.sm }}>
          {selectedNote?.pinned && (
            <i className="fa-solid fa-thumbtack" style={{ fontSize: typography.body2.fontSize, color: theme.palette.prime.orange }} title="Default note"></i>
          )}
          {selectedNote?.title || 'My Notes'}
          {selectedNote?.template === 1 && (
            <Chip
              label="Template"
              size="small"
              sx={{
                ml: spacingTokens.sm,
                bgcolor: theme.palette.white.main,
                color: theme.palette.prime.orange,
                fontSize: typographyTokens.fontSizes.xxs,
                height: '20px',
              }}
            />
          )}
        </Typography>
        <Button
          variant="contained"
          size="small"
          sx={{
            bgcolor: theme.palette.prime.blue,
            textTransform: 'none',
            fontWeight: typographyTokens.fontWeights.semibold,
            fontSize: typography.caption.fontSize,
            '&:hover': { bgcolor: theme.palette.prime.blue },
          }}
          onClick={handleEditNote}
          disabled={!selectedNote && regularNotes.length === 0}
        >
          <i className="fa-solid fa-pen" style={{ marginRight: spacingTokens.sm, fontSize: typographyTokens.fontSizes.xxs }}></i>
          Edit
        </Button>
      </Box>

      {/* Note Content Preview (Read-only) */}
      <Box
        sx={{
          flex: 1,
          mx: spacingTokens.md,
          p: spacingTokens.md,
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.white.main,
          color: theme.palette.mode === 'dark' ? theme.palette.white.main : theme.palette.background.default,
          borderRadius: 1,
          border: `1px solid ${theme.palette.supportingAdditional.tealContainerStroke}`,
          overflow: 'auto',
        }}
      >
        {isLoading ? (
          <Typography color="text.secondary">Loading notes...</Typography>
        ) : selectedNote ? (
          <Box
            dangerouslySetInnerHTML={{ __html: selectedNote.content }}
            sx={{
              '& p': { margin: `0 0 ${spacingTokens.sm} 0` },
              '& ul, & ol': { margin: `0 0 ${spacingTokens.sm} 0`, paddingLeft: '20px' },
              fontSize: typography.body2.fontSize,
              color: theme.palette.mode === 'dark' ? theme.palette.white.main : theme.palette.prime.granite,
            }}
          />
        ) : regularNotes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: spacingTokens.xl }}>
            <Typography color="text.secondary" sx={{ mb: spacingTokens.md }}>
              No notes yet for this SEP.
            </Typography>
            <Button
              variant="outlined"
              sx={{
                color: theme.palette.prime.blue,
                borderColor: theme.palette.prime.blue,
                textTransform: 'none',
              }}
              onClick={handleNewNote}
            >
              Create your first note
            </Button>
          </Box>
        ) : (
          <Typography color="text.secondary">Select a note to view</Typography>
        )}
      </Box>

      {/* Last Updated */}
      {selectedNote && (
        <Box sx={{ px: spacingTokens.md, py: spacingTokens.sm }}>
          <Typography variant="caption" sx={{ color: theme.palette.prime.granite, fontSize: typographyTokens.fontSizes.xs }}>
            Last updated: {formatDate(selectedNote.updatedAt)}
          </Typography>
        </Box>
      )}

      <Divider sx={{ mx: spacingTokens.md }} />

      {/* Templates Section at Bottom */}
      <Box sx={{ px: spacingTokens.md, py: spacingTokens.sm }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: spacingTokens.sm }}>
            <Typography variant="caption" sx={{ fontWeight: typographyTokens.fontWeights.semibold, color: theme.palette.prime.granite, textTransform: 'uppercase' }}>
              Templates ({templates.length})
            </Typography>
            <Button
              size="small"
              sx={{
                color: theme.palette.prime.orange,
                textTransform: 'none',
                fontWeight: typographyTokens.fontWeights.semibold,
                fontSize: typographyTokens.fontSizes.xs,
                p: 0,
                minWidth: 'auto',
              }}
              onClick={handleNewTemplate}
              disabled={isCreating || isUpdating}
            >
              <i className="fa-solid fa-plus" style={{ marginRight: spacingTokens.xs, fontSize: typographyTokens.fontSizes.xxs }}></i>
              Add New Template
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacingTokens.sm, maxHeight: '100px', overflowY: 'auto' }}>
            {templates.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No templates yet.
              </Typography>
            ) : (
              templates.map((template) => {
                const isActive = template.id === selectedNoteId;
                const templateDisplayTitle = template.title || 'Untitled Template';
                return (
                  <Box key={template.id} sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: spacingTokens.xs }}>
                          {templateDisplayTitle}
                          <Tooltip title="Use this template" arrow placement="top">
                            <Box
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUseTemplate(template);
                              }}
                              sx={{
                                ml: spacingTokens.xs,
                                cursor: 'pointer',
                                '&:hover': { color: theme.palette.prime.orange },
                              }}
                            >
                              <i className="fa-solid fa-copy" style={{ fontSize: typographyTokens.fontSizes.xxs }}></i>
                            </Box>
                          </Tooltip>
                        </Box>
                      }
                      size="small"
                      onClick={() => handleSelectNote(template.id)}
                      disabled={isDeleting}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isActive ? theme.palette.prime.orange : theme.palette.white.light,
                        color: isActive ? theme.palette.white.main : theme.palette.prime.orange,
                        fontWeight: isActive ? typographyTokens.fontWeights.semibold : typographyTokens.fontWeights.regular,
                        px: spacingTokens.sm,
                        pr: spacingTokens.lg,
                        fontSize: typography.caption.fontSize,
                        '&:hover': {
                          bgcolor: isActive ? theme.palette.prime.orange : theme.palette.white.light,
                          color: isActive ? theme.palette.white.main : theme.palette.prime.orange,
                        },
                      }}
                    />
                    <Tooltip title="Delete" arrow placement="top">
                      <Box
                        className="delete-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNote(template.id, true);
                        }}
                        sx={{
                          position: 'absolute',
                          right: 4,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: theme.palette.elements.alert,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.5,
                          transition: 'opacity 0.2s, background-color 0.2s',
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 1,
                            bgcolor: theme.palette.deepRed.dark,
                          },
                        }}
                      >
                        <i className="fa-solid fa-times" style={{ fontSize: typographyTokens.fontSizes.micro, color: theme.palette.white.main }}></i>
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box
        sx={{
          width: { xs: '100vw', sm: 680 },
          maxWidth: '100vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.supportingAdditional.tealUltraLight,
        }}
      >
        {viewMode === 'editor' ? renderEditorView() : renderListView()}
      </Box>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmation.show} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: spacingTokens.md }}>
            Are you sure you want to delete this {deleteConfirmation.isTemplate ? 'template' : 'note'}?
          </Typography>
          <Box sx={{ mt: spacingTokens.md, p: spacingTokens.md, bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.supportingAdditional.tealUltraLight, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: theme.palette.prime.granite, display: 'block', mb: spacingTokens.sm }}>
              {deleteConfirmation.isTemplate ? 'Template' : 'Note'}:
            </Typography>
            <Typography sx={{ fontWeight: typographyTokens.fontWeights.semibold, color: theme.palette.prime.blue, mb: spacingTokens.sm }}>
              "{deleteConfirmation.title}"
            </Typography>
            {!deleteConfirmation.isTemplate && (
              <Typography variant="caption" sx={{ color: theme.palette.prime.granite }}>
                SEP #<span style={{ fontWeight: typographyTokens.fontWeights.bold }}>{row.id}</span>
              </Typography>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: theme.palette.elements.alert, display: 'block', mt: spacingTokens.md }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} sx={{ color: theme.palette.prime.blue, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: theme.palette.elements.alert,
              textTransform: 'none',
              '&:hover': { bgcolor: theme.palette.deepRed.dark },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default SepNotesDrawer;
